import React, { useState, useEffect, useRef } from 'react';
import './Profile.css';
import { useAuth } from '../../contexts/authContext';
import supabase from "../../supabaseClient";
import { extractEmbedFromContent } from '../../utils/embed';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useParams } from 'react-router-dom';

// Import modular components
import ProfileHeader from './components/ProfileHeader';
import ProfileTabs from './components/ProfileTabs';
import AboutTab from './components/AboutTab';
import PostsTab from './components/PostsTab';
import MediaTab from './components/MediaTab';
// ContactTab import removed
import ProfessionalTab from './components/ProfessionalTab';
import Notification from './components/Notification';
import ConnectionsTab from './components/ConnectionsTab';

const Profile = () => {
  const { user } = useAuth();
  const { id: routeId } = useParams();
  const profileId = routeId || user?.id || null;
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('posts');
  const [contactPrivacy, setContactPrivacy] = useState({ email: false, phone: false });
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [newPost, setNewPost] = useState('');
  const [postMedia, setPostMedia] = useState(null);
  const [postMediaPreview, setPostMediaPreview] = useState(null);
  const [localEmbedPreview, setLocalEmbedPreview] = useState(null);
  const [isSubmittingPost, setIsSubmittingPost] = useState(false);
  const fileInputRef = useRef(null);
  const [editingPostId, setEditingPostId] = useState(null);
  const [editedPostContent, setEditedPostContent] = useState('');
  const [editedPostMediaPreview, setEditedPostMediaPreview] = useState(null);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [editedContact, setEditedContact] = useState({ phone: '', location: '' });
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [editedBio, setEditedBio] = useState('');
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [matchSuggestions, setMatchSuggestions] = useState([]);
  const [respondingMap, setRespondingMap] = useState({}); // connection_row_id -> true while updating
  const [findingMatches, setFindingMatches] = useState(false);
  const isOwnProfile = !!(user && profileId && user.id === profileId);

  useEffect(() => {
    if (!profileId) {
      setLoading(false);
      setProfile(null);
      return;
    }
    loadProfile();
  }, [profileId]);

  useEffect(() => {
    if (!profileId) return;
    loadPosts();
    loadConnections();
  }, [profileId]);

  useEffect(() => {
    if (profile) setEditedBio(profile.bio || '');
  }, [profile]);

  async function loadProfile() {
    setLoading(true);
    const { data, error } = await supabase
      .from('profiles')
      .select('id, display_name, name, email, avatar_url, occupation, interests, skills, native_languages, target_languages, allow_messages_from_anyone, phone, certificates, cvs, location, bio, professional_bio, date_of_birth, age') // include DOB and age
      .eq('id', profileId)
      .single();
    if (!error) {
      setProfile(data);
      setContactPrivacy(data.contactPrivacy || { email: false, phone: false });
    }
    setLoading(false);
  }

  async function loadPosts() {
    setLoadingPosts(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token || '';
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const userId = user?.id || '';
      const res = await fetch(`/api/community?includeReactions=true&ownerId=${profileId}&userId=${userId}`, { headers });
      if (!res.ok) throw new Error(`Failed to load posts: ${res.status}`);
      const { posts: serverPosts } = await res.json();
      // Map server format to local format
      const mapped = (serverPosts || []).map(p => ({
        id: p._id,
        content: p.content,
        media_url: p.media_url || null,
        media_type: p.media_type || null,
        embed_url: p.embed_url || null,
        source: p.source || 'profile',
        group: p.group || null,
        event: p.event || null,
        created_at: p.createdAt,
        owner_id: profileId,
        user_id: p.author?.id || profileId,
        reactionsByType: p.reactionsByType || {},
        userReactionType: p.userReactionType || null,
      }));
      setPosts(mapped);
    } catch (err) {
      console.error('Failed to load posts:', err);
      setPosts([]);
    }
    setLoadingPosts(false);
  }

  // --- Connections ---
  async function loadConnections() {
    if (!profileId) return;
    setLoadingConnections(true);
    // 1) fetch outbound (user is requester)
    const { data: outbound, error: outErr } = await supabase
      .from('connections')
      .select('id, user_id, connection_id, status, connection_type_id, created_at, updated_at')
      .eq('user_id', profileId);
    // 2) fetch inbound (user is recipient)
    const { data: inbound, error: inErr } = await supabase
      .from('connections')
      .select('id, user_id, connection_id, status, connection_type_id, created_at, updated_at')
      .eq('connection_id', profileId);
    if (outErr && inErr) {
      setConnections([]);
      setLoadingConnections(false);
      return;
    }
    // Combine with direction and compute the "best" row per other user
    const combined = [
      ...(outbound || []).map(r => ({ ...r, direction: 'outbound', other_id: r.connection_id })),
      ...(inbound || []).map(r => ({ ...r, direction: 'inbound', other_id: r.user_id }))
    ];

    if (combined.length === 0) {
      setConnections([]);
      setLoadingConnections(false);
      return;
    }

    const statusRank = (s) => {
      const v = String(s || '').toLowerCase();
      if (v === 'accepted') return 3;
      if (v === 'pending') return 2;
      if (v === 'rejected') return 1;
      return 0;
    };

    const bestByOther = {};
    for (const row of combined) {
      const curr = bestByOther[row.other_id];
      if (!curr) {
        bestByOther[row.other_id] = row;
        continue;
      }
      const currRank = statusRank(curr.status);
      const nextRank = statusRank(row.status);
      if (nextRank > currRank) {
        bestByOther[row.other_id] = row;
        continue;
      }
      if (nextRank === currRank) {
        const currTs = new Date(curr.updated_at || curr.created_at || 0).getTime();
        const nextTs = new Date(row.updated_at || row.created_at || 0).getTime();
        if (nextTs >= currTs) bestByOther[row.other_id] = row;
      }
    }

    const bestRows = Object.values(bestByOther);
    const ids = bestRows.map(r => r.other_id).filter(Boolean);
    if (ids.length === 0) {
      setConnections([]);
      setLoadingConnections(false);
      return;
    }

    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name, name, email, avatar_url, native_languages, target_languages, occupation, location, bio, interests')
      .in('id', ids);
    const pmap = {};
    (profiles || []).forEach(p => { pmap[p.id] = p; });

    const merged = bestRows.map(r => ({
      ...(pmap[r.other_id] || { id: r.other_id }),
      connection_row_id: r.id,
      status: r.status,
      connection_type_id: r.connection_type_id,
      created_at: r.created_at,
      updated_at: r.updated_at,
      direction: r.direction,
    }));

    setConnections(merged);
    setLoadingConnections(false);
  }

  // --- Posts handlers ---
  const handlePostSubmit = async (e) => {
    e.preventDefault();
    const raw = newPost.trim();
    const { content, embedUrl } = extractEmbedFromContent(raw);
    const embed_url = embedUrl || null;
    if (!content && !postMedia && !embed_url) return;
    setIsSubmittingPost(true);
    let media_url = null;
    let media_type = null;
    if (postMedia) {
      if (postMedia.type === 'embed') {
        media_url = postMediaPreview;
        media_type = 'embed';
      } else {
        media_url = postMediaPreview;
        media_type = postMedia.type?.startsWith('image/') ? 'image'
          : postMedia.type?.startsWith('video/') ? 'video'
          : null;
      }
    }
    const { data, error } = await supabase
      .from('posts')
      .insert([{
        user_id: user.id, // author
        owner_id: profileId, // wall owner
        content,
        media_url,
        media_type,
        embed_url
      }])
      .select()
      .single();
    if (!error) {
      // Notify wall owner if different from author
      if (user.id !== profileId) {
        try {
          await supabase.from('notifications').insert([{
            user_id: profileId,
            from_user_id: user.id,
            type: 'wall_post',
            title: 'New wall post',
            content: `${profile?.display_name || profile?.name || 'User'} posted on your wall`,
            is_read: false,
            data: { post_id: data.id, author_id: user.id }
          }]);
        } catch (e) {
          console.warn('Failed to insert wall_post notification', e);
        }
      }
      setPosts([data, ...posts]);
      setNewPost('');
      setLocalEmbedPreview(null);
      setPostMedia(null);
      setPostMediaPreview(null);
    }
    setIsSubmittingPost(false);
  };

  const handleMediaSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPostMedia(file);
    setPostMediaPreview(URL.createObjectURL(file));
  };

  const handleRemoveMedia = () => {
    setPostMedia(null);
    setPostMediaPreview(null);
  };

  const handleSaveEditPost = async (postId) => {
    const { error } = await supabase
      .from('posts')
      .update({ content: editedPostContent })
      .eq('id', postId);
    if (!error) {
      setPosts(posts =>
        posts.map(post =>
          post.id === postId ? { ...post, content: editedPostContent } : post
        )
      );
      setEditingPostId(null);
      setEditedPostContent('');
      setEditedPostMediaPreview(null);
    }
  };

  const handleCancelEditPost = () => {
    setEditingPostId(null);
    setEditedPostContent('');
    setEditedPostMediaPreview(null);
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    const { error } = await supabase
      .from('posts')
      .delete()
      .eq('id', postId);
    if (!error) {
      setPosts(posts => posts.filter(post => post.id !== postId));
    }
  };

  // --- Contact handlers ---
  const handleEditContactToggle = () => {
    if (isEditingContact) {
      updateContactInfo();
    } else {
      setEditedContact({
        phone: profile.phone || '',
        location: profile.location || ''
      });
    }
    setIsEditingContact(!isEditingContact);
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setEditedContact(prev => ({ ...prev, [name]: value }));
  };

  const updateContactInfo = async () => {
    // Validate and normalize phone to E.164, derive phone_country_code
    let phoneE164 = editedContact.phone || null;
    let phoneCountryCode = null;
    if (phoneE164) {
      if (!isValidPhoneNumber(phoneE164)) {
        alert('Please enter a valid phone number.');
        return;
      }
      const p = parsePhoneNumberFromString(phoneE164);
      if (p) phoneCountryCode = `+${p.countryCallingCode}`;
    }

    const { error: profileError } = await supabase
      .from('profiles')
      .update({
        phone: phoneE164,
        phone_country_code: phoneCountryCode,
        location: editedContact.location
      })
      .eq('id', user.id);
    if (!profileError) {
      await supabase.auth.updateUser({
        data: {
          phone: phoneE164,
          location: editedContact.location
        }
      });
      setProfile(prev => ({
        ...prev,
        phone: phoneE164,
        location: editedContact.location
      }));
    }
  };

  const toggleContactPrivacy = async (field) => {
    const newPrivacy = {
      ...contactPrivacy,
      [field]: !contactPrivacy[field]
    };
    setContactPrivacy(newPrivacy);
    await supabase
      .from('profiles')
      .update({ contactPrivacy: newPrivacy })
      .eq('id', user.id);
    await supabase.auth.updateUser({
      data: { contactPrivacy: newPrivacy }
    });
  };

  // --- Language formatting ---
  const formatLanguages = (languages) => {
    if (!languages) return [];
    if (Array.isArray(languages) && languages.length > 0 && typeof languages[0] === 'object' && languages[0].value) {
      return languages.map(lang => lang.label || lang.value);
    }
    return languages;
  };

  const nativeLanguages = formatLanguages(profile?.native_languages);
  // Fix the incomplete property access
  const targetLanguages = formatLanguages(profile?.target_languages);

  // Notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: '', type: '' }), 3500);
  };

  // --- Connection handlers ---
  const handleAddConnection = async (userToConnect) => {
    if (!userToConnect?.id) return;
    if (connections.some(conn => conn.id === userToConnect.id)) return;
    // Fetch connection type id if present (e.g., name = 'friend')
    let connection_type_id = null;
    const { data: types } = await supabase
      .from('connection_types')
      .select('id, name')
      .ilike('name', 'friend')
      .limit(1)
      .maybeSingle();
    if (types?.id) connection_type_id = types.id;

    // Insert pending connection
    const { data: insertRow, error } = await supabase
      .from('connections')
      .insert({
        user_id: user.id,
        connection_id: userToConnect.id,
        status: 'pending',
        connection_type_id
      })
      .select()
      .single();
    if (!error) {
      // Log request history (best-effort)
      await supabase.from('connection_request_history').insert({
        user_id: user.id,
        connection_id: userToConnect.id,
        action: 'requested',
        action_by: user.id
      });

      setConnections(prev => ([
        ...prev,
        {
          ...userToConnect,
          connection_row_id: insertRow.id,
          status: insertRow.status,
          connection_type_id: insertRow.connection_type_id,
          created_at: insertRow.created_at,
        }
      ]));
      showNotification('Connection request sent.', 'success');
    } else {
      showNotification('Failed to send connection request.', 'error');
    }
  };

  const handleRemoveConnection = async (userToRemove) => {
    setConnections(prev => prev.filter(conn => conn.id !== userToRemove.id));
    // Optionally, add logic to remove this connection from your database
  };

  // Respond to inbound request (accept/reject)
  const handleRespondConnection = async (conn, newStatus) => {
    if (!conn?.connection_row_id) return;
    setRespondingMap(prev => ({ ...prev, [conn.connection_row_id]: true }));
    const { error } = await supabase
      .from('connections')
      .update({ status: newStatus })
      .eq('id', conn.connection_row_id);
    if (!error) {
      await supabase.from('connection_request_history').insert({
        user_id: conn.direction === 'inbound' ? conn.id : user.id, // best effort
        connection_id: conn.direction === 'inbound' ? user.id : conn.id,
        action: newStatus,
        action_by: user.id
      });
      setConnections(prev => prev.map(c => c.connection_row_id === conn.connection_row_id ? { ...c, status: newStatus } : c));
      showNotification(newStatus === 'accepted' ? 'Connection accepted.' : 'Connection rejected.', 'success');
    } else {
      showNotification('Failed to update connection.', 'error');
    }
    setRespondingMap(prev => ({ ...prev, [conn.connection_row_id]: false }));
  };

  // --- Matching logic for language exchange ---
  const findLanguageMatches = async () => {
    if (findingMatches) return;
    setFindingMatches(true);
    // Fetch current user's languages
    const { data: myProfile } = await supabase
      .from('profiles')
      .select('id, native_languages, target_languages, occupation, interests')
      .eq('id', user.id)
      .single();

    // Fetch all other users (use correct column names)
    const { data: allProfiles } = await supabase
      .from('profiles')
      .select('id, display_name, name, email, avatar_url, native_languages, target_languages, occupation, interests')
      .neq('id', user.id);

    // Remove users already connected (any direction)
    const connectedIds = new Set(connections.map(c => c.id));

    // Helper to normalize arrays -> lowercase strings
    const arr = (val) => Array.isArray(val) ? val.map(v => String(v).toLowerCase()) : (val ? [String(val).toLowerCase()] : []);

    const myTarget = arr(myProfile?.target_languages);
    const myNative = arr(myProfile?.native_languages);

    const matches = (allProfiles || [])
      .filter(other => !connectedIds.has(other.id))
      .map(other => {
        const theirTarget = arr(other.target_languages);
        const theirNative = arr(other.native_languages);
        const iWant = myTarget.some(lang => theirNative.includes(lang));
        const theyWant = theirTarget.some(lang => myNative.includes(lang));
        let score = 0;
        if (iWant) score += 2;
        if (theyWant) score += 2;
        // Boost by shared interests/occupation
        if (myProfile?.occupation && other.occupation && myProfile.occupation === other.occupation) score += 1;
        if (myProfile?.interests && other.interests) {
          const myInts = arr(myProfile.interests);
          const theirInts = arr(other.interests);
          score += myInts.filter(i => theirInts.includes(i)).length;
        }
        return { ...other, mutual: iWant && theyWant, matchScore: score };
      })
      .filter(x => x.matchScore > 0)
      .sort((a, b) => b.matchScore - a.matchScore)
      .slice(0, 24);

    setMatchSuggestions(matches);
    showNotification(`Found ${matches.length} match${matches.length === 1 ? '' : 'es'}.`, 'success');
    setFindingMatches(false);
  };

  // --- Loading/Error States ---
  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading profile data...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-error">
        <p>You must be logged in to view your profile.</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-error">
        <p>Could not load profile data. Please try again later.</p>
      </div>
    );
  }

  // --- Main Render ---
  return (
    <div className="profile-container">
      {notification.message && (
        <Notification message={notification.message} type={notification.type} />
      )}
      <ProfileHeader
        profile={profile}
        user={user}
        supabase={supabase}
        onProfileUpdate={setProfile}
        showNotification={showNotification}
        isOwnProfile={isOwnProfile}
      />
      <ProfileTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      <div className="profile-body">
        {activeTab === 'about' && (
          <AboutTab
            profile={profile}
            isEditingBio={isEditingBio}
            setIsEditingBio={setIsEditingBio}
            editedBio={editedBio}
            setEditedBio={setEditedBio}
            setProfile={setProfile}
            user={user}
            supabase={supabase}
            nativeLanguages={nativeLanguages}
            targetLanguages={targetLanguages}
            isOwnProfile={isOwnProfile}
            isEditingContact={isEditingContact}
            handleEditContactToggle={handleEditContactToggle}
            editedContact={editedContact}
            handleContactChange={handleContactChange}
            toggleContactPrivacy={toggleContactPrivacy}
            contactPrivacy={contactPrivacy}
          />
        )}
        {activeTab === 'media' && (
          <MediaTab
            posts={posts}
            profile={profile}
            isLoading={loadingPosts}
            onSetProfilePicture={isOwnProfile ? async (mediaUrl) => {
              const { error } = await supabase
                .from('profiles')
                .update({ avatar_url: mediaUrl })
                .eq('id', user.id);
              if (!error) {
                setProfile(prev => ({ ...prev, avatar_url: mediaUrl }));
                showNotification('Profile picture updated!', 'success');
              } else {
                showNotification('Failed to update profile picture.', 'error');
              }
            } : undefined}
          />
        )}
        {activeTab === 'connections' && (
          <ConnectionsTab
            connections={connections}
            isOwnProfile={isOwnProfile}
            isLoading={loadingConnections}
            onRemoveConnection={isOwnProfile ? handleRemoveConnection : undefined}
            onFindMatches={isOwnProfile ? findLanguageMatches : undefined}
            matchSuggestions={matchSuggestions}
            onConnectWithSuggestion={isOwnProfile ? handleAddConnection : undefined}
            onRespondConnection={isOwnProfile ? handleRespondConnection : undefined}
            respondingMap={respondingMap}
            findLoading={findingMatches}
          />
        )}
        {activeTab === 'professional' && (
          <ProfessionalTab
            profile={profile}
            setProfile={setProfile}
            user={user}
            supabase={supabase}
            isOwnProfile={isOwnProfile}
          />
        )}
        {activeTab === 'posts' && (
          <PostsTab
            posts={posts}
            newPost={newPost}
            setNewPost={setNewPost}
            postMedia={postMedia}
            setPostMedia={setPostMedia}
            postMediaPreview={postMediaPreview}
            setPostMediaPreview={setPostMediaPreview}
            localEmbedPreview={localEmbedPreview}
            setLocalEmbedPreview={setLocalEmbedPreview}
            fileInputRef={fileInputRef}
            editingPostId={editingPostId}
            setEditingPostId={setEditingPostId}
            editedPostContent={editedPostContent}
            setEditedPostContent={setEditedPostContent}
            handleMediaSelect={handleMediaSelect}
            handleRemoveMedia={handleRemoveMedia}
            handlePostSubmit={handlePostSubmit}
            handleSaveEditPost={handleSaveEditPost}
            handleCancelEditPost={handleCancelEditPost}
            handleDeletePost={handleDeletePost}
            isSubmittingPost={isSubmittingPost}
            isLoading={loadingPosts}
            profile={profile}
            isOwnProfile={isOwnProfile}
          />
        )}
        </div>
    </div>
  );
};

export default Profile;